import { useCallback, useMemo, useState } from 'react';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';
import {useTranslation} from 'react-i18next';
import Print from '../../images/print.svg';
import Pdf from '../../images/pdf.svg';
import Excel from '../../images/newDesign/excel.svg';
import Send from "../../images/newDesign/send.svg";
import hashavshevetSvg from '../../images/newDesign/hashavshevet.svg';
import { useDispatch, useSelector } from 'react-redux';
import {hideLoader, showLoader} from '../../store/common/slice';
import {getBuildingTitleText} from '../../store/buildings/actions';
import {createPdfFromHtml} from '../shared/statelessMethods';
import {GAEvent} from '../google_analytics/google_analytics';
import { constants } from '../constants/constants';
import TableToExcel from '../utils/tableToExcel';
import PdfWrapper from './PdfWrapper';
import BllinkUtils from '../utils/utils';
import { exportToExcel } from '../utils/exportToExcel';
import { PdfIframe } from './PdfIframe';
import { getReportRows } from "./ReportData";
import { fileToBase64 } from "../../../utils/fileToBase64";
import { useAccessToken } from "../hooks/useAccessToken";
import { isAdminSelector } from "../../store/auth/selectors";
import { SendReportModal } from "./SendReportModal";
import { NotificationManager } from "react-notifications";

/**
 * @param classes
 * @param buildingID
 * @param partnerID
 * @param eventLabel
 * @param titleString
 * @param reportName
 * @param excelTitle
 * @param pdfContainer
 * @param hashavshevet
 * @param onHashavshevet
 * @param notTable
 * @param rows
 * @param titleRow
 * @param ignoreColumns
 * @param onClick
 * @param pdfIframe
 * @param {PdfSettings} pdfSettings
 * @param {XlsSettings} xlsSettings
 * @param address
 * @param hidePrint
 * */
export default function PrintPdfIcons({
    classes,
    buildingID,
    partnerID,
    eventLabel,
    titleString,
    reportName,
    excelTitle,
    pdfContainer,
    hashavshevet,
    onHashavshevet,
    notTable,
    rows,
    titleRow,
    ignoreColumns,
    onClick,
    pdfIframe,
    pdfSettings,
    xlsSettings,
    address,
    hidePrint
}) {
    const client = useAccessToken();
    const isAdmin = useSelector(isAdminSelector);
    const {t, i18n} = useTranslation();
    const dispatch = useDispatch();
    const [isPdfAvailable, setIsPdfAvailable] = useState(false);
    const [createPdf, setCreatePdf] = useState(false);
    const [emailData, setEmailData] = useState({});
    const [disablePdfSave, setDisablePdfSave] = useState(false);
    const [isSendReportModalOpen, setIsSendReportModalOpen] = useState(false);
    const titleData = useSelector(state => state.buildings.titleData);

    const onPrintClick = evt => {
        evt.preventDefault();
        if (onClick != null) onClick();
        GAEvent(constants.GoogleAnalytics.Events.clicks, 'print', 1, eventLabel);
        window.setTimeout(() => window.print(), 100);
    };

    const reportSubjectData = useMemo(() => {
        return `${reportName || ''} ${titleData?.translationKey ? t(`payments.${titleData.translationKey}`, titleData): ''}`.trim();
    }, [titleData, reportName])

    const onSaveFileClick = async (evt, fileType) => {
        evt.preventDefault();
        GAEvent(constants.GoogleAnalytics.Events.clicks, fileType, 1, eventLabel);
        let title = '';
        if (buildingID) {
            const titleData = await dispatch(getBuildingTitleText({t, buildingID}));
            title = titleData.payload || '';
        }
        const fileName = `${title} - ${excelTitle}`;
        if (notTable) {
            dispatch(showLoader());
            const data = await getReportRows(rows, 'xls');
            dispatch(hideLoader());
            if (data != null) {
                const rtl = i18n.language === 'he';
                exportToExcel(fileName, fileType, false, data, titleRow, ignoreColumns, xlsSettings?.columnTypes, rtl);
            }
        } else {
            TableToExcel.exportToExcel2(fileName, fileType);
        }
    };

    const onSaveCSVClick = async evt => {
        await onSaveFileClick(evt, 'csv');
    };

    const onSaveExcelClick = async evt => {
        await onSaveFileClick(evt, 'excel');
    };

    const onSavePdfClick = e => {
        e?.preventDefault();
        // new pdf infra
        if (pdfIframe) {
            setCreatePdf(true)
        } else {
            if (onClick != null) onClick();
            window.setTimeout(() => setIsPdfAvailable(true), 100);
        }
    };

    const onSendReportClick = e => {
        e.preventDefault();
        setIsSendReportModalOpen(true);
    };

    const sendingBlobFileByEmail = useCallback(async (pdfBlob) => {
        setDisablePdfSave(false);
        const file = await fileToBase64(pdfBlob);
        const fileSimpleName = await getFileName();
        const fileName = `${fileSimpleName}.pdf`;
        if (emailData?.email) {
            const result = await client.tenants.utils.sendReportByEmail(
                file,
                emailData.email,
                fileName,
                reportSubjectData,
                'application/pdf',
                emailData
            );
            if (result.sent) {
                NotificationManager.success(t('adminReports.reportSent'));
            }
        }
    }, [disablePdfSave, setIsSendReportModalOpen])

    /**
     * @param {boolean} noTitleData
     * @return {Promise<string>}
     */
    const getFileName = async (noTitleData = false) => {
        let title = '';
        if (buildingID) {
            const titleData = await dispatch(getBuildingTitleText({t, buildingID}));
            title = titleData.payload;
        }

        const currentDate = BllinkUtils.BllinkTime.parseDate(Date.now()).split('/').join('_');
        return `${currentDate}-${titleString}${noTitleData ? '' : ' - ' + title}`;
    }

    const onSavePdf = async container => {
        dispatch(showLoader());
        const title = await getFileName();
        const pdfBlob = await createPdfFromHtml(container, `${title}.pdf`, { save: !disablePdfSave });
        if (disablePdfSave) {
            await sendingBlobFileByEmail(pdfBlob);
        }
        dispatch(hideLoader());
        setIsPdfAvailable(false);
        GAEvent(constants.GoogleAnalytics.Events.clicks, 'downloadPdf', 1, eventLabel);
    };

    async function handleHashavshevetExportClick(evt) {
        GAEvent(constants.GoogleAnalytics.Events.clicks, 'HashavshevetExport', 1, eventLabel);
        onHashavshevet();
    }

    /**
     * @param {SelectionData} data
     * @return Promise<void>
     */
    const handleSendingDataSet = async (data) => {
        if (data) {
            setEmailData({
                ...data,
                reportName,
            });
            setDisablePdfSave(true);
            onSavePdfClick();
        }
    };

    const handlePostingDataDone = () => setCreatePdf(false);

    return (
        <div className={classes} style={{marginInlineStart: 'auto'}}>
            {!hidePrint && (<OverlayTrigger placement="top" overlay={props => <Tooltip {...props}>{t('reports.print_tooltip')}</Tooltip>}>
                <a href="/" className="pdff" onClick={onPrintClick}>
                    <img className="managerIcon" src={Print} alt="print" />
                </a>
            </OverlayTrigger>)}
            { hashavshevet && (
                <OverlayTrigger placement="top" overlay={props => <Tooltip {...props}>{t('reports.hashavshevet_tooltip')}</Tooltip>}>
                    <button className="pdff" onClick={handleHashavshevetExportClick}>
                        <img className="managerIcon" src={hashavshevetSvg} alt="hashavshevet" />
                    </button>
                </OverlayTrigger>
            ) }
            { excelTitle && (
                <OverlayTrigger placement="top" overlay={props => <Tooltip {...props}>{t('reports.excel_tooltip')}</Tooltip>}>
                    <a href="/" className="pdff" onClick={onSaveExcelClick}>
                        <img className="managerIcon" src={Excel} alt="excel" />
                    </a>
                </OverlayTrigger>
            ) }
            { excelTitle && (
                <OverlayTrigger placement="top" overlay={props => <Tooltip {...props}>{t('reports.csv_tooltip')}</Tooltip>}>
                    <a href="/" className="pdff" onClick={onSaveCSVClick}>
                        <img className="managerIcon" src={Excel} alt="csv" />
                    </a>
                </OverlayTrigger>
            ) }
            <OverlayTrigger placement="top" overlay={props => <Tooltip {...props}>{t('reports.download_tooltip')}</Tooltip>}>
                <a href="/" className="pdff" onClick={onSavePdfClick}>
                    <img className="managerIcon" src={Pdf} alt="download" />
                </a>
            </OverlayTrigger>
            {buildingID && isAdmin && <OverlayTrigger placement="top" overlay={props => <Tooltip {...props}>{t('reports.send')}</Tooltip>}>
                <a href="/" className="pdff" onClick={onSendReportClick}>
                    <img className="managerIcon" src={Send} alt="send" />
                </a>
            </OverlayTrigger>}
            {isPdfAvailable && <PdfWrapper component={pdfContainer} onSavePdf={onSavePdf}/>}
            <PdfIframe
                reportData={rows}
                disabled={!createPdf}
                disablePdfSave={disablePdfSave}
                onPdfGenerated={sendingBlobFileByEmail}
                onPostingDataDone={handlePostingDataDone}
                titleRow={titleRow}
                partnerID={partnerID}
                buildingAddress={address}
                reportTitle={titleString}
                pdfSettings={pdfSettings}
            />
            <SendReportModal
                buildingID={buildingID}
                show={isSendReportModalOpen}
                onClose={() => setIsSendReportModalOpen(false)}
                handleSelection={handleSendingDataSet}
            />
        </div>
    );
}
